import {
  BlockStack,
  Button,
  Icon,
  InlineError,
  InlineStack,
  LegacyStack,
  RadioButton,
  Text,
} from '@shopify/polaris'
import { InfoIcon } from '@shopify/polaris-icons'
import React, { useCallback } from 'react'

import { gidToId, MemoedTextField, useDialogActivator } from '../common/helpers'
import { HelpLabel } from '../common/HelpIcon'
import { useShopCurrency } from '../common/Price'
import { ProductVariantModal, ProductVariantResult } from '../common/ProductVariantModal'
import {
  normalizeWorkflowActionPrice,
  WorkflowAction,
  workflowActionLabels,
  WorkflowAddProductDataType,
  WorkflowProductDataType,
} from '../common/workflow-models'
import { WorkflowActionPriceFields } from './WorkflowActionPriceFields'
import { ProductBadge } from './WorkflowDescription'

export const AddProductAction = ({
  params,
  onChange,
  index,
  productError,
  currentPriceError,
  quantityError,
}: {
  params: WorkflowAddProductDataType
  onChange: (action: WorkflowAction, index: number) => any
  index: number
  productError: string | null
  currentPriceError: string | null
  quantityError: string | null
}) => {
  const shopCurrency = useShopCurrency()
  const productModalActivator = useDialogActivator()

  const [productData, quantity, currentPrice, recurringCycleLimit] = [
    (params[0] ?? []) as WorkflowProductDataType,
    params[1] ?? 1,
    params[2] ?? '0.00',
    params[3] === undefined ? null : params[3],
  ]

  const handleUpdate = useCallback(
    (params: WorkflowAddProductDataType) => {
      onChange(['add-product', params], index)
    },
    [index, onChange]
  )

  return (
    <>
      <LegacyStack vertical>
        <Text variant="headingSm" as="h3">
          {workflowActionLabels['add-product']}
        </Text>

        {productData.length === 0 && (
          <Text as="span" tone="subdued">
            No variant selected
          </Text>
        )}

        {productData.length > 0 && (
          <BlockStack gap="100" inlineAlign="start">
            <ProductBadge productData={productData} tone="success" />
            <InlineStack gap="100" wrap={false} align="start" blockAlign="start">
              <Icon source={InfoIcon} tone="caution" />
              <Text as="p" tone="caution">
                To prevent duplicate entries, this workflow action will NOT add this variant to
                subscription if it has already been added by any workflow.
              </Text>
            </InlineStack>
          </BlockStack>
        )}

        <Button onClick={productModalActivator.show} size="slim">
          Select product variant
        </Button>
        {productError && <InlineError message={productError} fieldID="" />}

        <BlockStack gap="200">
          <HelpLabel tooltip='Set the price per currency. Subscriptions are billed in their own currency, so this action applies only when a price for the subscription&apos;s currency is listed. Choose "Any currency" to use one price for every currency that is not listed. For pre-paid subscriptions, you need to manually multiply it by the amount of pre-paid deliveries.'>
            Unit price
          </HelpLabel>
          <WorkflowActionPriceFields
            prices={normalizeWorkflowActionPrice(currentPrice, shopCurrency)}
            onChange={(prices) =>
              handleUpdate([productData, quantity, prices, recurringCycleLimit])
            }
            error={currentPriceError}
            index={index}
            bannerText="This product will only be added to subscriptions billed in the currencies listed here."
          />
        </BlockStack>

        <MemoedTextField
          label="Quantity"
          type="number"
          autoComplete="off"
          value={quantity.toString()}
          min="1"
          step={1}
          onChange={(val: string) =>
            handleUpdate([productData, parseInt(val), currentPrice, recurringCycleLimit])
          }
          error={quantityError ?? undefined}
        />

        <LegacyStack vertical spacing="extraTight">
          <RadioButton
            label="Applies to this order only (removed automatically)"
            checked={recurringCycleLimit === 1}
            name={`recurringCycleLimit-${index}`}
            onChange={() => handleUpdate([productData, quantity, currentPrice, 1])}
          />
          <RadioButton
            label="Applies to this and all upcoming orders"
            checked={recurringCycleLimit === null}
            name={`recurringCycleLimit-${index}`}
            onChange={() => handleUpdate([productData, quantity, currentPrice, null])}
          />
        </LegacyStack>
      </LegacyStack>
      <ProductVariantModal
        currency={shopCurrency}
        open={productModalActivator.open}
        onClose={productModalActivator.close}
        onSelect={(selected: ProductVariantResult) => {
          handleUpdate([
            [gidToId(selected.id), selected.title],
            quantity,
            selected.price,
            recurringCycleLimit,
          ])
          productModalActivator.close()
        }}
      />
    </>
  )
}
